"use client";

import { useState, useEffect } from "react";
import { format, isToday, isTomorrow, differenceInDays } from "date-fns";
import Link from "next/link";
import {
  CalendarDays,
  TrendingUp,
  TrendingDown,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  ArrowRight,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileText,
  Users,
  Zap,
  BarChart3,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  mockPosts,
  mockTeam,
  mockClients,
  platformIcons,
  statusConfig,
} from "@/lib/mock-data";
import { useClient } from "@/lib/client-context";

const stats = [
  {
    label: "Total Reach",
    value: "124.5K",
    change: "+12.3%",
    trending: "up",
    icon: Eye,
    color: "text-blue-600",
    bg: "bg-blue-50",
  },
  {
    label: "Engagement",
    value: "8,432",
    change: "+8.7%",
    trending: "up",
    icon: Heart,
    color: "text-pink-600",
    bg: "bg-pink-50",
  },
  {
    label: "Posts This Month",
    value: "23",
    change: "+4",
    trending: "up",
    icon: FileText,
    color: "text-violet-600",
    bg: "bg-violet-50",
  },
  {
    label: "Team Members",
    value: "5",
    change: "2 active now",
    trending: "neutral",
    icon: Users,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
  },
];

const recentActivity = [
  {
    user: "Sarah Chen",
    initials: "SC",
    action: "approved",
    target: "Behind the scenes at our latest photoshoot...",
    time: "5 min ago",
    icon: CheckCircle2,
    iconColor: "text-green-500",
  },
  {
    user: "Marcus Johnson",
    initials: "MJ",
    action: "created",
    target: "New blog post: The Ultimate Guide to Content...",
    time: "23 min ago",
    icon: Plus,
    iconColor: "text-blue-500",
  },
  {
    user: "Alex Rivera",
    initials: "AR",
    action: "commented on",
    target: "5 tips to boost your social media presence...",
    time: "1 hour ago",
    icon: MessageCircle,
    iconColor: "text-violet-500",
  },
  {
    user: "Sarah Chen",
    initials: "SC",
    action: "scheduled",
    target: "Customer spotlight: See how @TechStartupXYZ...",
    time: "2 hours ago",
    icon: Clock,
    iconColor: "text-amber-500",
  },
  {
    user: "Jordan Lee",
    initials: "JL",
    action: "submitted for approval",
    target: "Hot take: The best marketing strategy is just...",
    time: "3 hours ago",
    icon: AlertCircle,
    iconColor: "text-orange-500",
  },
];

function getDateLabel(date: Date, now: Date) {
  if (isToday(date)) return "Today";
  if (isTomorrow(date)) return "Tomorrow";
  const diff = differenceInDays(date, now);
  if (diff < 0) return format(date, "MMM d");
  if (diff <= 7) return format(date, "EEEE");
  return format(date, "MMM d");
}

export default function DashboardPage() {
  const { selectedClientId } = useClient();
  const [today, setToday] = useState<Date | null>(null);

  useEffect(() => {
    setToday(new Date());
  }, []);

  const clientPosts = mockPosts.filter(
    (p) => selectedClientId === "all" || p.clientId === selectedClientId
  );

  const selectedClient = selectedClientId !== "all"
    ? mockClients.find((c) => c.id === selectedClientId)
    : null;

  const upcomingPosts = clientPosts
    .filter((p) => p.status === "SCHEDULED" || p.status === "APPROVED")
    .sort((a, b) => a.scheduledAt.getTime() - b.scheduledAt.getTime());

  const pendingApproval = clientPosts.filter(
    (p) => p.status === "PENDING_APPROVAL"
  );
  const drafts = clientPosts.filter((p) => p.status === "DRAFT");
  const publishedPosts = clientPosts.filter((p) => p.status === "PUBLISHED");

  const activeMembers = mockTeam.filter(
    (m) => m.lastActive === "Just now" || m.lastActive.includes("min")
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-5 border-b bg-card">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            {selectedClient ? selectedClient.name : "Dashboard"}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {today ? format(today, "EEEE, MMMM d, yyyy") : " "}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link href="/content/calendar">
            <Button variant="outline">
              <CalendarDays className="h-4 w-4 mr-2" />
              View Calendar
            </Button>
          </Link>
          <Button className="shadow-sm shadow-primary/25">
            <Plus className="h-4 w-4 mr-2" />
            New Post
          </Button>
        </div>
      </div>

      <div className="flex-1 p-8 overflow-auto space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <Card key={stat.label}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div
                    className={cn(
                      "h-10 w-10 rounded-lg flex items-center justify-center",
                      stat.bg
                    )}
                  >
                    <stat.icon className={cn("h-5 w-5", stat.color)} />
                  </div>
                  {stat.trending !== "neutral" && (
                    <div
                      className={cn(
                        "flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full",
                        stat.trending === "up"
                          ? "bg-green-50 text-green-700"
                          : "bg-red-50 text-red-700"
                      )}
                    >
                      {stat.trending === "up" ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : (
                        <TrendingDown className="h-3 w-3" />
                      )}
                      {stat.change}
                    </div>
                  )}
                  {stat.trending === "neutral" && (
                    <span className="text-xs text-muted-foreground">
                      {stat.change}
                    </span>
                  )}
                </div>
                <p className="text-2xl font-bold">{stat.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {stat.label}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upcoming Posts */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Upcoming Posts</CardTitle>
                <Link href="/content/posts" className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground transition-colors">
                  View all <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="pt-2 space-y-2">
              {upcomingPosts.map((post) => {
                const platform = platformIcons[post.platform];
                const status = statusConfig[post.status];
                return (
                  <Link
                    href={`/content/posts/${post.id}`}
                    key={post.id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                  >
                    <div className="shrink-0 text-center w-14">
                      <p className="text-[11px] font-semibold text-primary">
                        {today
                          ? getDateLabel(post.scheduledAt, today)
                          : format(post.scheduledAt, "MMM d")}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {format(post.scheduledAt, "h:mm a")}
                      </p>
                    </div>
                    <div className="h-10 w-px bg-border shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{post.content}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className={cn(
                            "text-[10px] font-medium px-1.5 py-0.5 rounded",
                            platform?.bg,
                            platform?.color
                          )}
                        >
                          {platform?.label}
                        </span>
                        <span
                          className={cn(
                            "text-[10px] font-medium px-1.5 py-0.5 rounded",
                            status.bg,
                            status.color
                          )}
                        >
                          {status.label}
                        </span>
                      </div>
                    </div>
                    <Avatar className="h-7 w-7 shrink-0">
                      <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                        {post.authorInitials}
                      </AvatarFallback>
                    </Avatar>
                  </Link>
                );
              })}
            </CardContent>
          </Card>

          {/* Quick Stats Sidebar */}
          <div className="space-y-6">
            {/* Needs Attention */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Needs Attention</CardTitle>
              </CardHeader>
              <CardContent className="pt-2 space-y-2">
                <Link
                  href="/content/posts"
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-amber-50 flex items-center justify-center">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Pending Approval</p>
                      <p className="text-[11px] text-muted-foreground">
                        {pendingApproval.length} post
                        {pendingApproval.length !== 1 ? "s" : ""} waiting
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant="secondary"
                    className="bg-amber-50 text-amber-700"
                  >
                    {pendingApproval.length}
                  </Badge>
                </Link>

                <Link
                  href="/content/posts"
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-gray-100 flex items-center justify-center">
                      <FileText className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Drafts</p>
                      <p className="text-[11px] text-muted-foreground">
                        {drafts.length} unfinished post
                        {drafts.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary">{drafts.length}</Badge>
                </Link>

                <Link
                  href="/content/posts"
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Published</p>
                      <p className="text-[11px] text-muted-foreground">
                        {publishedPosts.length} post
                        {publishedPosts.length !== 1 ? "s" : ""} live
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant="secondary"
                    className="bg-emerald-50 text-emerald-700"
                  >
                    {publishedPosts.length}
                  </Badge>
                </Link>
              </CardContent>
            </Card>

            {/* Active Team */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Active Now</CardTitle>
                  <Link href="/team" className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground transition-colors">
                    View team <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="pt-2 space-y-2">
                {activeMembers.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-3 p-2 rounded-lg"
                  >
                    <div className="relative">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-[10px] bg-primary/10 text-primary font-semibold">
                          {member.initials}
                        </AvatarFallback>
                      </Avatar>
                      <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-green-500 border-2 border-card" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{member.name}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {member.lastActive}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="space-y-1">
              {recentActivity.map((activity, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  <div className="relative">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="text-[10px] bg-primary/10 text-primary font-semibold">
                        {activity.initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-card flex items-center justify-center">
                      <activity.icon
                        className={cn("h-3 w-3", activity.iconColor)}
                      />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-medium">{activity.user}</span>
                      <span className="text-muted-foreground">
                        {" "}
                        {activity.action}{" "}
                      </span>
                      <span className="font-medium">{activity.target}</span>
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {activity.time}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Platform Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(platformIcons).slice(0, 4).map(([key, platform]) => {
            const platformPosts = clientPosts.filter((p) => p.platform === key);
            const published = platformPosts.filter(
              (p) => p.status === "PUBLISHED"
            );
            const scheduled = platformPosts.filter(
              (p) => p.status === "SCHEDULED"
            );
            return (
              <Card
                key={key}
                className="hover:shadow-md transition-shadow cursor-pointer"
              >
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={cn(
                        "h-10 w-10 rounded-lg flex items-center justify-center",
                        platform.bg
                      )}
                    >
                      <span
                        className={cn("text-sm font-bold", platform.color)}
                      >
                        {platform.label.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{platform.label}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {platformPosts.length} posts
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-emerald-500" />
                      <span className="text-muted-foreground">
                        {published.length} published
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-blue-500" />
                      <span className="text-muted-foreground">
                        {scheduled.length} scheduled
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
